import {ChangeDetectionStrategy, Component, inject, Input} from '@angular/core';
import {
  BaseDialogDefinition,
  ConfirmationCallback,
  DEFAULT_BASE_CANCEL_BUTTON_LABEL,
  DEFAULT_BASE_CONFIRM_BUTTON_LABEL,
  DialogAppConfig,
  DialogCancelButtonType,
  ValueCalculator
} from '@aviary/dialogs/base-dialog/base-dialog.types';
import {MxSelector} from '@aviary/store/store.types';
import {asSelector} from '@aviary/store/store.helpers';
import {MatDialogRef} from '@angular/material/dialog';
import {ActionButtonsComponent} from '@aviary/components/action-buttons/action-buttons.component';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {NgClass, NgTemplateOutlet} from '@angular/common';
import {FilledButtonComponent} from '@aviary/buttons/filled-button/filled-button.component';
import {TextButtonComponent} from '@aviary/buttons/text-button/text-button.component';
import {OutlinedButtonComponent} from '@aviary/buttons/outlined-button/outlined-button.component';
import {DIALOG_APP_CONFIG} from '@aviary/injection-tokens/app-provided.tokens';
import {MatDivider} from '@angular/material/list';
import {MatProgressSpinner} from '@angular/material/progress-spinner';

@Component({
  selector: 'mx-base-dialog',
  host: {
    class: 'hk-delegate-vertical-scrolling grow'
  },
  templateUrl: './base-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ActionButtonsComponent, SelectPipe, NgTemplateOutlet, FilledButtonComponent, TextButtonComponent, OutlinedButtonComponent, MatDivider, MatProgressSpinner,
    NgClass
  ]
})
export class BaseDialogComponent<ValueType> {

  @Input()
  public set definition(definition: BaseDialogDefinition<ValueType>) {
    this.titleSelector = asSelector(definition.title);
    this.descriptionSelector = asSelector(definition.description ?? '');
    this.promptSelector = asSelector(definition.prompt ?? '');
    this.cancelButtonLabelSelector = asSelector(definition.cancelButtonLabel ?? DEFAULT_BASE_CANCEL_BUTTON_LABEL);
    this.confirmButtonLabelSelector = asSelector(definition.confirmButtonLabel ?? DEFAULT_BASE_CONFIRM_BUTTON_LABEL);
    this.onConfirm = definition.onConfirm;
    this.valueCalculator = definition.valueCalculator ?? (() => 'Missing value calculator!') as unknown as ValueCalculator<ValueType>;
  }

  @Input() public showConfirmButton: boolean = true;
  @Input() public enableConfirmButton: boolean = true;

  @Input() public showCancelButton: boolean = true;
  @Input() public enableCancelButton: boolean = true;

  @Input() public showAdditionalActionButtons: boolean = false;
  @Input() public additionalButtonsAreConfirm: boolean = false;

  // Used primarily by PersistentFormDialogComponent, and should not be used otherwise without good reason.
  @Input() public closeOnConfirm: boolean = true;

  /*
    These "confirmation pending" inputs are used primarily by PersistentFormDialogComponent, and should not be used otherwise without good reason.
    They change only the appearance and enablement of the confirmation button, not the flow of the dialog itself.
  */
  @Input() public confirmationPending: boolean = false;
  @Input() public showConfirmationPendingSpinner: boolean = true;

  public titleSelector: MxSelector<string>;
  public descriptionSelector: MxSelector<string>;
  public promptSelector: MxSelector<string>;
  public cancelButtonLabelSelector: MxSelector<string>;
  public confirmButtonLabelSelector: MxSelector<string>;

  private dialogAppConfig: DialogAppConfig = inject(DIALOG_APP_CONFIG);
  public cancelButtonType: DialogCancelButtonType = this.dialogAppConfig.cancelButtonType;
  public showActionsDivider: boolean = this.dialogAppConfig.showActionsDivider;

  private readonly dialog: MatDialogRef<BaseDialogComponent<ValueType>, ValueType> = inject(MatDialogRef);
  private onConfirm?: ConfirmationCallback;
  private valueCalculator: ValueCalculator<ValueType>;

  public onClickCancelButton(): void {
    if (!this.enableCancelButton) return;

    this.dialog.close();
  }

  public onClickConfirmButton(): void {
    if (!this.enableConfirmButton || this.confirmationPending) return;

    this.onConfirm?.();
    if (this.closeOnConfirm) this.dialog.close(this.valueCalculator());
  }
}
